import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Plus, Award, MessageCircle, Building, User, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API } from "@/lib/api";


export default function EmployeeSpotlight({ eventId, isOwner }) {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [featuredEmployee, setFeaturedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [congratName, setCongratName] = useState(localStorage.getItem("employee_congrat_name") || "");
  const [congratMessages, setCongratMessages] = useState({});
  
  const [newEmployee, setNewEmployee] = useState({
    name: "",
    role: "",
    department: "",
    bio: "",
    achievements: [""]
  });

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
    fetchFeaturedEmployee();
  }, [eventId]);

  const fetchEmployees = async (dept = null) => {
    try {
      const url = dept && dept !== "all" 
        ? `${API}/events/${eventId}/employees?department=${dept}`
        : `${API}/events/${eventId}/employees`;
      const response = await axios.get(url);
      setEmployees(response.data);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error("Failed to fetch departments:", error);
    }
  };

  const fetchFeaturedEmployee = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/employees/featured`);
      setFeaturedEmployee(response.data);
    } catch (error) {
      console.error("Failed to fetch featured employee:", error);
    }
  };

  const handleCreateEmployee = async () => {
    if (!newEmployee.name.trim() || !newEmployee.role.trim() || !newEmployee.department.trim()) {
      toast.error("Name, Rolle und Abteilung erforderlich!");
      return;
    }

    const validAchievements = newEmployee.achievements.filter(a => a.trim() !== "");

    try {
      await axios.post(
        `${API}/events/${eventId}/employees`,
        { ...newEmployee, achievements: validAchievements },
        { withCredentials: true }
      );
      toast.success("Mitarbeiter hinzugefügt!");
      setNewEmployee({
        name: "",
        role: "",
        department: "",
        bio: "",
        achievements: [""]
      });
      fetchEmployees();
      fetchDepartments();
    } catch (error) {
      toast.error("Fehler beim Hinzufügen");
    }
  };

  const handlePhotoUpload = async (employeeId, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      toast.loading("Foto wird hochgeladen...", { id: "photo-upload" });
      await axios.post(
        `${API}/events/${eventId}/employees/${employeeId}/photo`,
        formData,
        { 
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" }
        }
      );
      toast.success("Foto hochgeladen!", { id: "photo-upload" });
      fetchEmployees();
    } catch (error) {
      toast.error("Fehler beim Hochladen", { id: "photo-upload" });
    }
  };

  const handleFeatureEmployee = async (employeeId) => {
    try {
      await axios.patch(
        `${API}/events/${eventId}/employees/${employeeId}/feature`,
        {},
        { withCredentials: true }
      );
      toast.success("Als Mitarbeiter des Monats ausgezeichnet! ⭐");
      fetchEmployees();
      fetchFeaturedEmployee();
    } catch (error) {
      toast.error("Fehler beim Auszeichnen");
    }
  };

  const handleCongratulate = async (employeeId) => {
    const message = congratMessages[employeeId];
    if (!message || !message.trim()) {
      toast.error("Bitte Nachricht eingeben!");
      return;
    }
    if (!congratName.trim()) {
      toast.error("Bitte deinen Namen eingeben!");
      return;
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/employees/${employeeId}/congratulate`,
        {
          author_name: congratName,
          message: message
        }
      );
      toast.success("Glückwunsch gesendet! 🎉");
      localStorage.setItem("employee_congrat_name", congratName);
      setCongratMessages({ ...congratMessages, [employeeId]: "" });
      fetchEmployees();
    } catch (error) {
      toast.error("Fehler beim Senden");
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (!confirm("Mitarbeiter wirklich löschen?")) return;

    try {
      await axios.delete(
        `${API}/events/${eventId}/employees/${employeeId}`,
        { withCredentials: true }
      );
      toast.success("Mitarbeiter gelöscht");
      fetchEmployees();
      fetchDepartments();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleDepartmentFilter = (dept) => {
    setSelectedDepartment(dept);
    fetchEmployees(dept);
  };

  const addAchievement = () => {
    setNewEmployee({
      ...newEmployee,
      achievements: [...newEmployee.achievements, ""]
    });
  };

  const updateAchievement = (index, value) => {
    const updated = [...newEmployee.achievements];
    updated[index] = value;
    setNewEmployee({ ...newEmployee, achievements: updated });
  };

  const removeAchievement = (index) => {
    const updated = newEmployee.achievements.filter((_, i) => i !== index);
    setNewEmployee({ ...newEmployee, achievements: updated });
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <Star className="w-5 h-5 text-primary" />
          Mitarbeiter-Spotlight
        </h3>
        <p className="text-sm text-muted-foreground">
          Herausragende Mitarbeiter würdigen und feiern! ⭐
        </p>
      </div>

      {isOwner ? (
        <Tabs defaultValue="employees" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="employees">Mitarbeiter</TabsTrigger>
            <TabsTrigger value="add">Hinzufügen</TabsTrigger>
          </TabsList>

          {/* Employees Tab */}
          <TabsContent value="employees" className="space-y-4">
            {/* Department Filter */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleDepartmentFilter("all")}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedDepartment === "all"
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary hover:bg-secondary/80'
                }`}
              >
                Alle
              </button>
              {departments.map(dept => (
                <button
                  key={dept}
                  onClick={() => handleDepartmentFilter(dept)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedDepartment === dept
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary hover:bg-secondary/80'
                  }`}
                >
                  {dept}
                </button>
              ))}
            </div>

            {/* Featured Employee */}
            {featuredEmployee && (
              <div className="p-6 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-2 border-yellow-500 rounded-lg">
                <div className="flex items-center gap-2 mb-4">
                  <Award className="w-6 h-6 text-yellow-600" />
                  <h4 className="text-lg font-bold">⭐ Mitarbeiter des Monats</h4>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 bg-secondary rounded-full overflow-hidden flex-shrink-0">
                    {featuredEmployee.photo_url ? (
                      <img
                        src={`${BACKEND_URL}${featuredEmployee.photo_url}`}
                        alt={featuredEmployee.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-12 h-12 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h5 className="text-xl font-bold mb-1">{featuredEmployee.name}</h5>
                    <p className="text-sm text-muted-foreground mb-2">{featuredEmployee.role} • {featuredEmployee.department}</p>
                    {featuredEmployee.bio && <p className="text-sm mb-3">{featuredEmployee.bio}</p>}
                    {featuredEmployee.achievements && featuredEmployee.achievements.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Erfolge:</p>
                        <ul className="text-sm space-y-1">
                          {featuredEmployee.achievements.map((achievement, idx) => (
                            <li key={idx}>🏆 {achievement}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Employee List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {employees.length === 0 ? (
                <div className="col-span-2 text-center py-12 text-muted-foreground">
                  <User className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Noch keine Mitarbeiter</p>
                </div>
              ) : (
                employees.map((employee) => (
                  <div key={employee.id} className="p-4 border rounded-lg bg-card">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-16 h-16 bg-secondary rounded-full overflow-hidden flex-shrink-0">
                        {employee.photo_url ? (
                          <img
                            src={`${BACKEND_URL}${employee.photo_url}`}
                            alt={employee.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h5 className="font-semibold flex items-center gap-2">
                          {employee.name}
                          {employee.is_featured && <Star className="w-4 h-4 text-yellow-500 fill-current" />}
                        </h5>
                        <p className="text-xs text-muted-foreground">{employee.role}</p>
                        <p className="text-xs text-primary flex items-center gap-1 mt-1">
                          <Building className="w-3 h-3" />
                          {employee.department}
                        </p>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => handleDeleteEmployee(employee.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {employee.bio && <p className="text-sm mb-3">{employee.bio}</p>}

                    {employee.achievements && employee.achievements.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-muted-foreground mb-1">Erfolge:</p>
                        {employee.achievements.map((achievement, idx) => (
                          <p key={idx} className="text-xs">• {achievement}</p>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2 pt-3 border-t">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id={`photo-${employee.id}`}
                        onChange={(e) => handlePhotoUpload(employee.id, e.target.files[0])}
                      />
                      <label htmlFor={`photo-${employee.id}`} className="flex-1">
                        <Button size="sm" variant="outline" className="w-full" asChild>
                          <span>
                            <Upload className="w-3 h-3 mr-2" />
                            Foto
                          </span>
                        </Button>
                      </label>
                      {!employee.is_featured && (
                        <Button
                          size="sm"
                          onClick={() => handleFeatureEmployee(employee.id)}
                          className="flex-1"
                        >
                          <Star className="w-3 h-3 mr-2" />
                          Feature
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Add Employee Tab */}
          <TabsContent value="add" className="space-y-4">
            <div className="p-4 bg-secondary/20 rounded-lg space-y-3">
              <h4 className="font-medium">👤 Neuen Mitarbeiter hinzufügen</h4>
              
              <div>
                <Label>Name *</Label>
                <Input
                  value={newEmployee.name}
                  onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
                  placeholder="z.B. Max Mustermann"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Rolle *</Label>
                  <Input
                    value={newEmployee.role}
                    onChange={(e) => setNewEmployee({ ...newEmployee, role: e.target.value })}
                    placeholder="z.B. Senior Developer"
                  />
                </div>
                <div>
                  <Label>Abteilung *</Label>
                  <Input
                    value={newEmployee.department}
                    onChange={(e) => setNewEmployee({ ...newEmployee, department: e.target.value })}
                    placeholder="z.B. IT"
                  />
                </div>
              </div>

              <div>
                <Label>Bio</Label>
                <Textarea
                  value={newEmployee.bio}
                  onChange={(e) => setNewEmployee({ ...newEmployee, bio: e.target.value })}
                  placeholder="Kurze Beschreibung..."
                  rows={2}
                />
              </div>

              <div>
                <Label>Erfolge/Achievements</Label>
                {newEmployee.achievements.map((achievement, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      value={achievement}
                      onChange={(e) => updateAchievement(index, e.target.value)}
                      placeholder={`Erfolg ${index + 1}`}
                      className="flex-1"
                    />
                    {newEmployee.achievements.length > 1 && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeAchievement(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addAchievement}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Erfolg hinzufügen
                </Button>
              </div>

              <Button onClick={handleCreateEmployee} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Mitarbeiter hinzufügen
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      ) : (
        <div className="space-y-6">
          {/* Featured Employee for Guests */}
          {featuredEmployee && (
            <div className="p-6 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-2 border-yellow-500 rounded-lg">
              <div className="flex items-center gap-2 mb-4">
                <Award className="w-6 h-6 text-yellow-600" />
                <h4 className="text-lg font-bold">⭐ Mitarbeiter des Monats</h4>
              </div>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-24 h-24 bg-secondary rounded-full overflow-hidden flex-shrink-0">
                  {featuredEmployee.photo_url ? (
                    <img
                      src={`${BACKEND_URL}${featuredEmployee.photo_url}`}
                      alt={featuredEmployee.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-12 h-12 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h5 className="text-xl font-bold mb-1">{featuredEmployee.name}</h5>
                  <p className="text-sm text-muted-foreground mb-2">{featuredEmployee.role} • {featuredEmployee.department}</p>
                  {featuredEmployee.bio && <p className="text-sm mb-3">{featuredEmployee.bio}</p>}
                  {featuredEmployee.achievements && featuredEmployee.achievements.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Erfolge:</p>
                      <ul className="text-sm space-y-1">
                        {featuredEmployee.achievements.map((achievement, idx) => (
                          <li key={idx}>🏆 {achievement}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Congratulations for featured */}
              {featuredEmployee.congratulations && featuredEmployee.congratulations.length > 0 && (
                <div className="mb-3 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Glückwünsche:</p>
                  {featuredEmployee.congratulations.map((congrat, idx) => (
                    <div key={idx} className="p-2 bg-white/50 rounded text-sm">
                      <p className="font-medium text-xs text-primary">{congrat.author_name}</p>
                      <p>{congrat.message}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  placeholder="Dein Name"
                  value={congratName}
                  onChange={(e) => setCongratName(e.target.value)}
                  className="flex-1"
                />
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="Glückwunsch-Nachricht..."
                  value={congratMessages[featuredEmployee.id] || ""}
                  onChange={(e) => setCongratMessages({ ...congratMessages, [featuredEmployee.id]: e.target.value })}
                  className="flex-1"
                />
                <Button onClick={() => handleCongratulate(featuredEmployee.id)}>
                  <MessageCircle className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Department Filter */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => handleDepartmentFilter("all")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedDepartment === "all"
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary hover:bg-secondary/80'
              }`}
            >
              Alle
            </button>
            {departments.map(dept => (
              <button
                key={dept}
                onClick={() => handleDepartmentFilter(dept)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedDepartment === dept
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary hover:bg-secondary/80'
                }`}
              >
                {dept}
              </button>
            ))}
          </div>

          {/* Employee Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {employees.length === 0 ? (
              <div className="col-span-2 text-center py-12 text-muted-foreground">
                <User className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Keine Mitarbeiter in dieser Kategorie</p>
              </div>
            ) : (
              employees.map((employee) => (
                <div key={employee.id} className="p-4 border rounded-lg bg-card">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-16 h-16 bg-secondary rounded-full overflow-hidden flex-shrink-0">
                      {employee.photo_url ? (
                        <img
                          src={`${BACKEND_URL}${employee.photo_url}`}
                          alt={employee.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold flex items-center gap-2">
                        {employee.name}
                        {employee.is_featured && <Star className="w-4 h-4 text-yellow-500 fill-current" />}
                      </h5>
                      <p className="text-xs text-muted-foreground">{employee.role}</p>
                      <p className="text-xs text-primary flex items-center gap-1 mt-1">
                        <Building className="w-3 h-3" />
                        {employee.department}
                      </p>
                    </div>
                  </div>

                  {employee.bio && <p className="text-sm mb-3">{employee.bio}</p>}

                  {employee.achievements && employee.achievements.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Erfolge:</p>
                      {employee.achievements.map((achievement, idx) => (
                        <p key={idx} className="text-xs">• {achievement}</p>
                      ))}
                    </div>
                  )}

                  {/* Congratulations */}
                  {employee.congratulations && employee.congratulations.length > 0 && (
                    <div className="mb-3 space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Glückwünsche:</p>
                      {employee.congratulations.slice(0, 2).map((congrat, idx) => (
                        <div key={idx} className="text-xs bg-secondary/20 p-2 rounded">
                          <span className="font-medium text-primary">{congrat.author_name}:</span> {congrat.message}
                        </div>
                      ))}
                      {employee.congratulations.length > 2 && (
                        <p className="text-xs text-muted-foreground">+{employee.congratulations.length - 2} weitere...</p>
                      )}
                    </div>
                  )}

                  {/* Add Congratulation */}
                  <div className="space-y-2 pt-3 border-t">
                    <Input
                      placeholder="Dein Name"
                      value={congratName}
                      onChange={(e) => setCongratName(e.target.value)}
                      className="text-sm h-8"
                    />
                    <div className="flex gap-2">
                      <Input
                        placeholder="Gratuliere..."
                        value={congratMessages[employee.id] || ""}
                        onChange={(e) => setCongratMessages({ ...congratMessages, [employee.id]: e.target.value })}
                        className="flex-1 text-sm h-8"
                      />
                      <Button size="sm" onClick={() => handleCongratulate(employee.id)}>
                        <MessageCircle className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
